import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, Heart, ArrowUpRight, ChatCircleText, WhatsappLogo, FacebookLogo, ShareNetwork } from "@phosphor-icons/react";

// Rally deadline: Saturday, Oct 31, 2026 at 11:59 PM Eastern Time (EDT = UTC-4)
const RALLY_DEADLINE = new Date("2026-11-01T03:59:00Z").getTime();

const SHARE_URL = "https://secure.qgiv.com/for/gmoadcfr/";
const SHARE_MESSAGE = "Join Greater Mount Olive AME Church's $1 Dollar-Thon — one dollar, one family fed. Give today:";
const SHARE_FULL = `${SHARE_MESSAGE} ${SHARE_URL}`;

const SHARE_LINKS = [
    {
        key: "sms",
        label: "Text",
        href: `sms:?&body=${encodeURIComponent(SHARE_FULL)}`,
        Icon: ChatCircleText,
        color: "bg-[#4C1D95] hover:bg-[#2E1A47] text-white",
    },
    {
        key: "whatsapp",
        label: "WhatsApp",
        href: `https://wa.me/?text=${encodeURIComponent(SHARE_FULL)}`,
        Icon: WhatsappLogo,
        color: "bg-[#25D366] hover:bg-[#1DA851] text-white",
    },
    {
        key: "facebook",
        label: "Facebook",
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(SHARE_MESSAGE)}`,
        Icon: FacebookLogo,
        color: "bg-[#1877F2] hover:bg-[#125FBE] text-white",
    },
];

function ShareRow({ idPrefix }) {
    return (
        <div className="mb-8" data-testid={`${idPrefix}-share`}>
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 mb-3">
                <ShareNetwork size={14} weight="bold" className="text-[#FFC72C]" /> Share the Rally
            </p>
            <div className="flex flex-wrap gap-2">
                {SHARE_LINKS.map(({ key, label, href, Icon, color }) => (
                    <a
                        key={key}
                        href={href}
                        target={key === "sms" ? undefined : "_blank"}
                        rel="noopener noreferrer"
                        data-testid={`${idPrefix}-share-${key}`}
                        className={`inline-flex items-center gap-2 ${color} px-4 py-2.5 text-[11px] font-black uppercase tracking-widest transition-colors shadow-md`}
                    >
                        <Icon size={16} weight="fill" /> {label}
                    </a>
                ))}
            </div>
        </div>
    );
}

function getRemaining() {
    const diff = RALLY_DEADLINE - Date.now();
    if (diff <= 0) return null;
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
}

function CountdownUnit({ value, label, testid }) {
    const padded = String(value).padStart(2, "0");
    return (
        <div className="flex flex-col items-center" data-testid={testid}>
            <div className="bg-[#FFC72C] text-[#2E1A47] font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none px-3 py-3 sm:px-4 sm:py-4 min-w-[72px] sm:min-w-[88px] text-center shadow-lg tabular-nums">
                {padded}
            </div>
            <span className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">{label}</span>
        </div>
    );
}

function Countdown({ idPrefix }) {
    const [t, setT] = useState(getRemaining);
    useEffect(() => {
        const id = setInterval(() => setT(getRemaining()), 1000);
        return () => clearInterval(id);
    }, []);
    if (!t) return null;
    return (
        <div className="mb-8" data-testid={`${idPrefix}-countdown`}>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 mb-3">Rally ends Saturday, Oct 31, 2026</p>
            <div className="flex items-center gap-2 sm:gap-3">
                <CountdownUnit value={t.days} label="Days" testid={`${idPrefix}-countdown-days`} />
                <span className="font-display font-black text-3xl sm:text-4xl text-[#FFC72C]/60 pb-6">:</span>
                <CountdownUnit value={t.hours} label="Hours" testid={`${idPrefix}-countdown-hours`} />
                <span className="font-display font-black text-3xl sm:text-4xl text-[#FFC72C]/60 pb-6">:</span>
                <CountdownUnit value={t.minutes} label="Minutes" testid={`${idPrefix}-countdown-minutes`} />
                <span className="font-display font-black text-3xl sm:text-4xl text-[#FFC72C]/60 pb-6">:</span>
                <CountdownUnit value={t.seconds} label="Seconds" testid={`${idPrefix}-countdown-seconds`} />
            </div>
        </div>
    );
}

function RallyRecap({ sectionId }) {
    const photos = [
        "/photos/volunteer-food-tables.jpg",
        "/photos/volunteer-circle.jpg",
        "/photos/food-distribution.jpg",
        "/photos/volunteer-hug.jpg",
        "/photos/volunteer-team.jpg",
        "/photos/home-food-pantry.jpg",
    ];
    return (
        <section
            id={sectionId}
            className="relative overflow-hidden bg-gradient-to-br from-[#2E1A47] via-[#4C1D95] to-[#2E1A47] scroll-mt-24"
            data-testid="rally-recap-section"
        >
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FFC72C] blur-3xl" />
                <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#8B5A9F] blur-3xl" />
            </div>
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center text-white">
                <div className="inline-flex items-center gap-2 bg-[#FFC72C]/15 border border-[#FFC72C]/40 px-3 py-1.5 mb-8">
                    <Heart size={12} weight="fill" className="text-[#FFC72C]" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC72C]">Fall Rally 2026 · Recap</span>
                </div>

                <h2 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl mb-8" data-testid="rally-recap-title">
                    Thank you,<br />
                    <span className="text-[#FFC72C] italic">church family</span>.
                </h2>

                <p className="text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-6">
                    Our Fall Rally $1 Dollar-Thon has ended — and because of your faithful generosity, hope has been multiplied across West Philadelphia. Every single dollar carried a prayer, a meal, and a message of love.
                </p>
                <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto mb-12">
                    You gave cheerfully. You gave together. You gave like a people rooted in faith — and the fruit will be felt for seasons to come. To God be the glory.
                </p>

                {/* Scripture */}
                <div className="max-w-3xl mx-auto mb-14 relative">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-20 h-[2px] bg-[#FFC72C]" />
                    <p className="font-display italic text-2xl sm:text-3xl lg:text-4xl text-white leading-snug mb-5">
                        &ldquo;Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver.&rdquo;
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFC72C]">2 Corinthians 9:7</p>
                </div>

                {/* Photos */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-12" data-testid="rally-recap-gallery">
                    {photos.map((src, i) => (
                        <div key={src} className={`relative overflow-hidden ring-1 ring-white/10 shadow-xl group ${i === 0 || i === 5 ? "aspect-[4/5]" : "aspect-square"}`}>
                            <img
                                src={src}
                                alt="Fall Rally memory"
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/60 via-transparent to-transparent" />
                        </div>
                    ))}
                </div>

                <Link
                    to="/give"
                    data-testid="rally-recap-give-link"
                    className="inline-flex items-center justify-center gap-3 bg-[#FFC72C] hover:bg-white text-[#2E1A47] px-10 py-5 text-sm font-black uppercase tracking-widest transition-colors shadow-xl"
                >
                    Keep the Mission Going <ArrowUpRight size={16} weight="bold" />
                </Link>
            </div>
        </section>
    );
}

/**
 * FallRally — reusable Dollar-Thon section.
 * Auto-switches to RallyRecap after the deadline passes.
 */
export default function FallRally({ idPrefix = "fall-rally", sectionId }) {
    const [active, setActive] = useState(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            if (params.get("rallyPreview") === "recap") return false;
            if (params.get("rallyPreview") === "active") return true;
        }
        return Date.now() < RALLY_DEADLINE;
    });
    useEffect(() => {
        if (!active) return;
        const id = setInterval(() => {
            if (Date.now() >= RALLY_DEADLINE) setActive(false);
        }, 1000);
        return () => clearInterval(id);
    }, [active]);
    if (!active) return <RallyRecap sectionId={sectionId} />;

    return (
        <section
            id={sectionId}
            className="relative overflow-hidden bg-gradient-to-br from-[#2E1A47] via-[#4C1D95] to-[#2E1A47] scroll-mt-24"
            data-testid={`${idPrefix}-section`}
        >
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FFC72C] blur-3xl" />
                <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#8B5A9F] blur-3xl" />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#FFC72C] via-[#E6B327] to-[#FFC72C] opacity-70 blur-md group-hover:opacity-90 transition" />
                            <div className="relative aspect-video overflow-hidden bg-black ring-1 ring-white/10 shadow-2xl">
                                <video
                                    data-testid={`${idPrefix}-video`}
                                    src="/videos/fall-rally-dollar-thon.mp4"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    className="w-full h-full object-contain bg-black"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2 text-white">
                        <div className="inline-flex items-center gap-2 bg-[#FFC72C]/15 border border-[#FFC72C]/40 px-3 py-1.5 mb-6">
                            <span className="w-1.5 h-1.5 bg-[#FFC72C] rounded-full animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC72C]">Now · Fall Rally 2026</span>
                        </div>

                        <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-5" data-testid={`${idPrefix}-title`}>
                            The <span className="text-[#FFC72C]">$1</span><br />
                            Dollar-Thon
                        </h2>
                        <p className="text-white/85 text-base lg:text-lg leading-relaxed mb-4 max-w-lg">
                            One dollar. One family fed. One act of faith that multiplies.
                        </p>
                        <p className="text-white/70 text-sm lg:text-base leading-relaxed mb-8 max-w-lg">
                            Join us as we rally together to <span className="text-[#FFC72C] font-semibold">Feed Hope</span> across West Philadelphia. Every dollar strengthens our mission of worship, outreach, and community care.
                        </p>

                        <Countdown idPrefix={idPrefix} />
                        <ShareRow idPrefix={idPrefix} />

                        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                            <a
                                href="https://secure.qgiv.com/for/gmoadcfr/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${idPrefix}-give-btn`}
                                className="inline-flex items-center justify-center gap-3 bg-[#FFC72C] hover:bg-white text-[#2E1A47] px-8 py-5 text-sm font-black uppercase tracking-widest transition-colors shadow-xl"
                            >
                                <HandHeart size={20} weight="fill" /> Give $1 – Feed Hope <ArrowUpRight size={16} weight="bold" />
                            </a>
                            <a
                                href="https://gofund.me/33427c600"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${idPrefix}-gofundme-btn`}
                                className="inline-flex items-center justify-center gap-3 bg-white hover:bg-[#FFC72C] text-[#2E1A47] px-8 py-5 text-sm font-black uppercase tracking-widest transition-colors shadow-xl"
                            >
                                <Heart size={18} weight="fill" className="text-[#4C1D95]" /> Give via GoFundMe <ArrowUpRight size={16} weight="bold" />
                            </a>
                            <Link
                                to="/give"
                                data-testid={`${idPrefix}-learn-btn`}
                                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white px-6 py-5 text-xs font-bold uppercase tracking-widest transition-colors border border-white/30"
                            >
                                More Ways to Give
                            </Link>
                        </div>

                        <p className="mt-6 text-[11px] uppercase tracking-[0.25em] font-bold text-white/50">
                            Secure giving via Qgiv · 100% to Fall Rally
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
